import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { MigratedStudent, StudentsApi, ExportFormat } from '@/services/school.api';
import { ReportsApi } from '@/services/reports.api';
import { AcademicClassesApi, AcademicYearsApi, isNoCurrentYearError } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { formatDate } from '@/lib/format';

const columnHelper = createColumnHelper<MigratedStudent>();

/**
 * Legacy "Students Report" columns (`students_report.blade.php`, read
 * directly rather than assumed): name, DOB, class_section, username, roll,
 * gender, admission_date — no guardian/attendance/fee. That's the existing
 * Students list plus gender/DOB/username, which live on `users` via the
 * same join the list already does — extended there, not a parallel query.
 * Export reuses the Data Export CSV/Excel (also extended with the same 2
 * columns) rather than a new export endpoint.
 */
export function StudentsReportPage() {
  const term = useTerminology();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'admissionNo', defaultSortOrder: 'asc' });
  const [yearId, setYearId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [includeLeft, setIncludeLeft] = useState(false);
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: currentYear, error: currentYearError } = useQuery({
    queryKey: ['academic-years', 'current'],
    queryFn: AcademicYearsApi.current,
    retry: (failureCount, err) => !isNoCurrentYearError(err) && failureCount < 3,
  });
  const noCurrentYear = isNoCurrentYearError(currentYearError);

  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'students-list', dt.params, yearId, classId, sectionId, includeLeft],
    queryFn: () =>
      StudentsApi.list({
        ...dt.params,
        academicYearId: yearId || currentYear?.id || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        includeLeft,
      }),
    enabled: !noCurrentYear && !!(yearId || currentYear),
    placeholderData: keepPreviousData,
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await ReportsApi.exportStudents(format);
    } finally {
      setBusy(null);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('admissionNo', { header: 'Admission No' }),
    columnHelper.accessor('name', { header: 'Name', cell: (ctx) => ctx.getValue() ?? '(no linked user)' }),
    columnHelper.accessor('gender', { header: 'Gender', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('dob', { header: 'DOB', cell: (ctx) => (ctx.getValue() ? formatDate(ctx.getValue()) : '—') }),
    columnHelper.accessor('username', { header: 'Username', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.display({
      id: 'class',
      header: `${term.level} · ${term.group}`,
      cell: (ctx) => (ctx.row.original.className ? `${ctx.row.original.className} · ${ctx.row.original.sectionName}` : '—'),
    }),
    columnHelper.accessor('rollNumber', { header: 'Roll', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('enrollmentStatus', {
      header: 'Status',
      cell: (ctx) =>
        ctx.getValue() === 'continue' ? (
          <Badge tone="green">Active</Badge>
        ) : ctx.getValue() === 'left' ? (
          <Badge tone="slate">Left</Badge>
        ) : (
          '—'
        ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Students Report"
        description="Full student listing with gender, DOB, and username alongside the usual class/section/status."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('csv')}
              disabled={!!busy}
              title="Export to CSV"
            >
              {busy === 'csv' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-slate-600" />}
              CSV
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
          </div>
        }
      />

      {noCurrentYear ? (
        <div className="card p-8 text-center text-slate-500">This school has no current academic year set.</div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search by name or admission #"
                className="pl-9"
              />
            </div>
            <Select className="!w-44" value={yearId || currentYear?.id || ''} onChange={(e) => setYearId(e.target.value)}>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.name}
                </option>
              ))}
            </Select>
            <Select
              className="!w-40"
              value={classId}
              onChange={(e) => {
                setClassId(e.target.value);
                setSectionId('');
              }}
            >
              <option value="">All {term.level}</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Select className="!w-40" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
              <option value="">All {term.group}</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              <input type="checkbox" checked={includeLeft} onChange={(e) => setIncludeLeft(e.target.checked)} />
              Include Left
            </label>
          </div>

          <DataTable
            columns={columns}
            result={data}
            state={dt}
            loading={isFetching}
            emptyMessage="No students found."
            getRowId={(row) => row.id}
          />
        </>
      )}
    </>
  );
}
